import {
    GenericFileDownloadDto,
    GenericResponseFileDto,
    GenericTableDataFileDto,
} from 'inside-fifa/api/generic-tables/dto';
import { useCallback, useMemo, useState } from 'react';
import { getFiles } from 'inside-fifa/api/generic-tables/get-files';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { SearchableDataFileTableColumnConfig } from './consts';
import useAuth from 'hooks/useAuth';
import { isUserInAllowedGroup } from 'utils/route-guard/roles-check';
import { INSIDE_FIFA_TEAM_ADMIN } from 'inside-fifa/setup/auth';
import { useIntl } from 'react-intl';
import { createTimestampColumn } from '../../../../components/columns/timestamp-column';
import { SheetsCell } from './partials/columns/sheets-column';
import { Column, Renderer } from 'react-table';
import { ActionsCell } from './partials/columns/action-cell';
import { CellProps } from 'react-table';
import { deleteApiFile } from 'inside-fifa/api/generic-tables/delete-file';
import { downloadApiFile } from 'inside-fifa/api/generic-tables/download-file';

type ConfigDataFile = {
    files: GenericTableDataFileDto[];
    pageCount: number;
    fetchFiles: BaseTableFetchFuncProps;
    refreshList: any;
    isManagementAllowed: any;
    deleteFile: (file: GenericTableDataFileDto) => Promise<GenericResponseFileDto>;
    downloadFile: (file: GenericTableDataFileDto) => Promise<GenericFileDownloadDto>;
};

export const useFileManagement = (): ConfigDataFile => {
    const [files, setFiles] = useState<GenericTableDataFileDto[]>([]);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(0);

    const { user } = useAuth();
    const isManagementAllowed = isUserInAllowedGroup(user?.groups ?? [], [INSIDE_FIFA_TEAM_ADMIN]) || true;

    const fetchFiles: BaseTableFetchFuncProps = useCallback(async ({ pageIndex, pageSize, filters, searchTerm }) => {
        try {
            const response = await getFiles({
                pageIndex,
                pageSize,
                andSpecifications: filters ? mapFiltersToSpecification(filters) : undefined,
                orSpecifications:
                    searchTerm && searchTerm.length > 0
                        ? mapSearchParamToSpecification(searchTerm, SearchableDataFileTableColumnConfig)
                        : undefined,
            });

            setFiles(response.elements);
            setPageCount(Math.ceil(response.total / pageSize));
            setCurrentPage(pageIndex);
            setPageSize(pageSize);
        } catch {
            setFiles([]);
            setPageCount(0);
        }
    }, []);

    const refreshList = useCallback(
        async () => fetchFiles({ pageIndex: currentPage, pageSize, filters: [] }),
        [currentPage, fetchFiles, pageSize],
    );
    const deleteFile = useCallback(
        async (file: GenericTableDataFileDto): Promise<GenericResponseFileDto> => {
            try {
                const response = await deleteApiFile(file);
                if (response) {
                    await refreshList();
                    return {
                        status: response.status,
                        message: `File ${file.name} has been deleted.`,
                    };
                }
            } catch (error: any) {
                if (error) {
                    const errorStatus = error.status;
                    const errorMessage = error.data?.message || 'An unexpected error occurred.';
                    const errorData = error.data?.data || '';
                    return {
                        status: errorStatus,
                        message: errorMessage,
                        data: errorData,
                    };
                } else {
                    return {
                        status: 500,
                        message: 'Unexpected error',
                    };
                }
            }
            throw new Error('Failed to delete file');
        },
        [refreshList],
    );
    const downloadFile = useCallback(async (file: GenericTableDataFileDto): Promise<GenericFileDownloadDto> => {
        try {
            const res = await downloadApiFile(file);
            if (res) {
                return {
                    file: res.file,
                    url: res.url,
                    a: res.a,
                    status: res.status,
                };
            }
        } catch (error: any) {
            throw new Error('Failed to download file');
        }
        throw new Error('Failed to download file');
    }, []);
    return {
        files,
        pageCount,
        fetchFiles,
        refreshList,
        isManagementAllowed,
        deleteFile,
        downloadFile,
    };
};

type UseDataTableColumnsConfigDeps = {
    onDelete: (file: GenericTableDataFileDto) => Promise<GenericTableDataFileDto>;
    onDownload: (file: GenericTableDataFileDto) => Promise<GenericFileDownloadDto>;
};

export const useFileListColumnsConfig = ({ onDelete, onDownload }: UseDataTableColumnsConfigDeps): Column[] => {
    const intl = useIntl();
    const { user } = useAuth();

    return useMemo(() => {
        const baseColumns: Column[] = [
            {
                Header: intl.formatMessage({ id: 'inside-fifa.generic-tables.file-id' }),
                accessor: 'fileId',
            },
            {
                Header: intl.formatMessage({ id: 'inside-fifa.generic-tables.file-name' }),
                accessor: 'name',
            },
            {
                Header: intl.formatMessage({ id: 'inside-fifa.generic-tables.upload-date' }),
                accessor: 'uploadDateTimestamp',
                Cell: createTimestampColumn('uploadDateTimestamp'),
            },
            {
                Header: intl.formatMessage({ id: 'inside-fifa.generic-tables.sheets' }),
                accessor: 'sheets',
                Cell: SheetsCell,
            },
        ] as Column[];

        if (isUserInAllowedGroup(user?.groups ?? [], [INSIDE_FIFA_TEAM_ADMIN])) {
            baseColumns.push({
                Header: intl.formatMessage({ id: 'inside-fifa.actions-header' }),
                disableFilters: true,
                Cell: (({ row }: CellProps<GenericTableDataFileDto>) => {
                    return <ActionsCell row={row} onDelete={onDelete} onDownload={onDownload} />;
                }) as unknown as Renderer<CellProps<{}>>,
            });
        }

        return baseColumns;
    }, [intl, onDelete, onDownload, user]);
};
